import * as React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { DOMMouseMoveTracker } from 'dom-lib';
import { defaultClassPrefix, getUnhandledProps } from './utils';
import { addEvent, removeEvent } from './utils/domFns';
import TableContext from './TableContext';
import { RESIZE_MIN_WIDTH } from './constants';
import { transformZoomData } from '../_util/DocumentUtils';
const propTypeKeys = [
    'height',
    'defaultColumnWidth',
    'columnLeft',
    'columnFixed',
    'className',
    'classPrefix',
    'minWidth',
    'style',
    'onColumnResizeStart',
    'onColumnResizeEnd',
    'onColumnResizeMove',
    'onMouseEnterHandler',
    'onMouseLeaveHandler',
];
class ColumnResizeHandler extends React.Component {
    constructor(props) {
        super(props);
        this.columnWidth = 0;
        this.cursorDelta = 0;
        this.mounted = false;
        this.deltaX = 0;
        this.touchX = 0;
        this.handleDragStart = (e) => {
            if (e.touches) {
                this.dragging = true;
                this.cursorDelta = 0;
                const eTouch = e.touches[0];
                const clientX = transformZoomData(eTouch.clientX);
                const clientY = transformZoomData(eTouch.clientY);
                const client = {
                    clientX,
                    clientY,
                };
                this.touchX = clientX;
                const { onColumnResizeStart } = this.props;
                if (onColumnResizeStart) {
                    onColumnResizeStart(client);
                }
            }
            const thisNode = this.findDOMNode();
            const { ownerDocument } = thisNode;
            if (e.type === 'touchstart')
                e.preventDefault();
            if (!this.mounted)
                return;
            addEvent(ownerDocument, 'touchmove', this.handleDrag);
            addEvent(ownerDocument, 'touchend', this.handleDragStop);
        };
        this.handleDrag = (event) => {
            if (event.touches) {
                const x = transformZoomData(event.touches[0].clientX);
                this.deltaX = x - this.touchX;
                this.touchX = x;
                this.onMove(this.deltaX);
            }
        };
        this.handleDragStop = () => {
            if (!this.dragging)
                return;
            this.dragging = false;
            const { onColumnResizeEnd } = this.props;
            if (onColumnResizeEnd) {
                onColumnResizeEnd(this.columnWidth, this.cursorDelta);
            }
        };
        this.onMove = (deltaX) => {
            if (!this.isKeyDown && !this.dragging) {
                return;
            }
            const { onColumnResizeMove, defaultColumnWidth, columnLeft, columnFixed } = this.props;
            const { rtl } = this.context;
            this.cursorDelta += deltaX;
            this.columnWidth = clamp(
            // @ts-ignore
            defaultColumnWidth + (rtl ? -this.cursorDelta : this.cursorDelta), this.props.minWidth ? Math.max(this.props.minWidth, RESIZE_MIN_WIDTH) : RESIZE_MIN_WIDTH, 20000);
            if (onColumnResizeMove) {
                onColumnResizeMove(this.columnWidth, columnLeft, columnFixed);
            }
        };
        this.onColumnResizeEnd = () => {
            this.isKeyDown = false;
            const { onColumnResizeEnd } = this.props;
            if (onColumnResizeEnd) {
                onColumnResizeEnd(this.columnWidth, this.cursorDelta);
            }
            const { mouseMoveTracker } = this;
            if (mouseMoveTracker) {
                if (mouseMoveTracker.releaseMouseMoves) {
                    mouseMoveTracker.releaseMouseMoves();
                }
                this.mouseMoveTracker = null;
            }
        };
        this.onColumnResizeMouseDown = (event) => {
            this.mouseMoveTracker = this.getMouseMoveTracker();
            this.mouseMoveTracker.captureMouseMoves(event);
            this.isKeyDown = true;
            this.cursorDelta = 0;
            const client = {
                clientX: transformZoomData(event.clientX),
                clientY: transformZoomData(event.clientY),
                preventDefault: Function(),
            };
            const { onColumnResizeStart } = this.props;
            if (onColumnResizeStart) {
                onColumnResizeStart(client);
            }
        };
        this.handleShowMouseArea = debounce(() => {
            const { onMouseEnterHandler = noop } = this.props;
            onMouseEnterHandler();
        }, 300);
        this.handleHideMouseArea = () => {
            const { onMouseLeaveHandler = noop } = this.props;
            this.handleShowMouseArea.cancel();
            onMouseLeaveHandler();
        };
        this.columnWidth = props.defaultColumnWidth || 0;
        this.handleRef = React.createRef();
    }
    static get contextType() {
        return TableContext;
    }
    componentDidMount() {
        this.mounted = true;
        // Touch handlers must be added with {passive: false} to be cancelable.
        const thisNode = this.findDOMNode();
        if (thisNode) {
            addEvent(thisNode, 'touchstart', this.handleDragStart, { passive: false });
        }
    }
    componentWillUnmount() {
        this.mounted = false;
        if (this.mouseMoveTracker) {
            this.mouseMoveTracker.releaseMouseMoves();
            this.mouseMoveTracker = null;
        }
        const thisNode = this.findDOMNode();
        if (thisNode) {
            const { ownerDocument } = thisNode;
            removeEvent(ownerDocument, 'touchmove', this.handleDrag);
            removeEvent(ownerDocument, 'touchend', this.handleDragStop);
            removeEvent(thisNode, 'touchstart', this.handleDragStart, { passive: false });
        }
    }
    // React Strict Mode compatibility: if `nodeRef` is passed, we will use it instead of trying to find
    // the underlying DOM node ourselves. See the README for more information.
    findDOMNode() {
        const { handleRef } = this;
        return handleRef && handleRef.current || ReactDOM.findDOMNode(this);
    }
    getMouseMoveTracker() {
        return (this.mouseMoveTracker ||
            new DOMMouseMoveTracker(this.onMove, this.onColumnResizeEnd, document.body));
    }
    render() {
        const { columnLeft = 0, classPrefix, height, className, style, columnFixed, ...rest } = this.props;
        if (columnFixed === 'right') {
            return null;
        }
        const { rtl } = this.context;
        const styles = {
            [rtl ? 'right' : 'left']: this.columnWidth + columnLeft - 2,
            height,
            ...style,
        };
        const classes = classNames(classPrefix, className);
        const unhandled = getUnhandledProps(propTypeKeys, rest);
        return (React.createElement("div", Object.assign({}, unhandled, { ref: this.handleRef, className: classes, style: styles, onMouseDown: this.onColumnResizeMouseDown, onTouchEnd: this.handleDragStop, onMouseEnter: this.handleShowMouseArea, onMouseLeave: this.handleHideMouseArea, role: "button", tabIndex: -1 })));
    }
}
ColumnResizeHandler.defaultProps = {
    classPrefix: defaultClassPrefix('performance-table-column-resize-spanner'),
};
export default ColumnResizeHandler;
